import { useCallback, useEffect, useRef, useState } from 'react';
import { darkTheme } from '../../lib/tokens';
import type { PostStatus, Reichweite, ReviewTarget } from './types';

const t = darkTheme.colors;

/**
 * Kopierfeld mit Rückmeldung AM FELD (CUR-324).
 *
 * **Bewusst kein Toast.** Der Toast ist das Hausmuster für Mutationen — selten,
 * wichtig, gehört in die Ecke. Hier wird sechsmal hintereinander kopiert, und
 * die Frage ist dann nicht mehr *dass* etwas passiert ist, sondern **welches
 * Feld**. Ein Toast beantwortet genau das nicht und stapelt sich obendrein.
 *
 * Der Knopf hat eine feste Mindestbreite, damit „Kopieren" und „Kopiert ✓"
 * nicht springen — ein Layout-Sprung unter dem Finger liest sich wie ein
 * Fehlklick.
 */
export function CopyField({
  label,
  value,
  limitWeich,
  limitHart,
  mono,
  scrollbar,
}: {
  label: string;
  value: string;
  limitWeich?: number;
  limitHart?: number;
  mono?: boolean;
  scrollbar?: boolean;
}): JSX.Element {
  const [kopiert, setKopiert] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  const kopieren = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setKopiert(true);
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setKopiert(false), 1600);
    } catch {
      // Ohne Zwischenablage-Recht bleibt nur der Hinweis — stillschweigend
      // nichts zu tun waere hier das Schlimmste: Man klebt dann den vorherigen
      // Inhalt ein und merkt es erst in der Upload-Maske.
      window.prompt('Zwischenablage nicht verfügbar — hier kopieren:', value);
    }
  }, [value]);

  const laenge = value.length;
  // Der Zaehler faerbt sich erst bei ÜBERSCHREITUNG der harten Grenze. Ein
  // Zaehler, der dauernd bunt ist, wird ignoriert; die weiche Grenze ist eine
  // Empfehlung und faerbt deshalb nicht.
  const ueber = limitHart !== undefined && laenge > limitHart;

  return (
    <div className="mb-4">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: t.textTertiary }}>
          {label}
        </span>
        <div className="flex items-center gap-2">
          {limitHart !== undefined && (
            <span className="text-xs tabular-nums" style={{ color: ueber ? t.warning : t.textTertiary }}>
              {laenge}
              {limitWeich !== undefined ? `/${limitWeich}` : ''}
              {limitHart !== limitWeich ? ` (max ${limitHart})` : ''}
            </span>
          )}
          <button
            type="button"
            onClick={kopieren}
            aria-label={`${label} kopieren`}
            className="min-h-[44px] min-w-[104px] rounded-md px-3 text-sm font-semibold"
            style={{
              backgroundColor: kopiert ? t.successBg : t.bgElevated,
              color: kopiert ? t.success : t.textPrimary,
            }}
          >
            {kopiert ? 'Kopiert ✓' : 'Kopieren'}
          </button>
        </div>
      </div>
      {/* Die Bestaetigung wird angesagt, sonst existiert sie fuer Screenreader
          nur als Farbe. */}
      <span role="status" className="sr-only">{kopiert ? `${label} kopiert` : ''}</span>
      <div
        className={`rounded-md border p-3 text-sm ${mono ? 'font-mono' : ''} ${scrollbar ? 'max-h-64 overflow-y-auto' : ''}`}
        style={{
          borderColor: kopiert ? t.accent : t.borderSubtle,
          backgroundColor: t.bgSurface,
          color: t.textSecondary,
          whiteSpace: 'pre-wrap',
        }}
      >
        {value}
      </div>
    </div>
  );
}

const STATUS_STIL: Record<PostStatus, { label: string; bg: string; fg: string; strike?: boolean }> = {
  GEPLANT: { label: 'Geplant', bg: t.infoBg, fg: t.info },
  BEREIT: { label: 'Bereit', bg: t.warningBg, fg: t.warning },
  GEPOSTET: { label: 'Gepostet', bg: t.successBg, fg: t.success },
  VERSCHOBEN: { label: 'Verschoben', bg: t.bgElevated, fg: t.textTertiary },
  VERWORFEN: { label: 'Verworfen', bg: t.bgElevated, fg: t.textTertiary, strike: true },
};

export function StatusPill({ status }: { status: PostStatus }): JSX.Element {
  const s = STATUS_STIL[status];
  return (
    <span
      className="inline-block rounded-full px-2 py-0.5 text-xs font-semibold"
      style={{ backgroundColor: s.bg, color: s.fg, textDecoration: s.strike ? 'line-through' : 'none' }}
    >
      {s.label}
    </span>
  );
}

/**
 * Kanalfarben aus den vorhandenen `topic*`-Tokens statt neuer Werte.
 *
 * **Semantische Farben bleiben damit frei für den Status.** Kanal und Zustand
 * dürfen sich nicht um dieselbe Farbe streiten — sonst liest man „grün" mal als
 * „gepostet" und mal als „Instagram".
 */
const KANAL_FARBE: Record<ReviewTarget, string> = {
  YOUTUBE: t.topicDiagnostics,
  INSTAGRAM: t.topicPharma,
  // Neutral mit Absicht: Jeder freie `topic*`-Ton trägt schon eine
  // Statusbedeutung (grün, rot, gelb) — TikTok bekäme sonst eine Aussage, die
  // es nicht hat (CUR-2248).
  TIKTOK: t.textPrimary,
  FACEBOOK: t.topicAnatomy,
  APP: t.textSecondary,
};

const KANAL_NAME: Record<ReviewTarget, string> = {
  YOUTUBE: 'YouTube',
  INSTAGRAM: 'Instagram',
  TIKTOK: 'TikTok',
  FACEBOOK: 'Facebook',
  APP: 'App-Fassung',
};

const KANAL_KURZ: Record<ReviewTarget, string> = {
  YOUTUBE: 'YT',
  INSTAGRAM: 'IG',
  TIKTOK: 'TT',
  FACEBOOK: 'FB',
  APP: 'App',
};

/**
 * Eine Tabelle statt Ternären. Die drei `c === 'YOUTUBE' ? … : 'Facebook'`
 * hätten TikTok still „Facebook" genannt (CUR-2248).
 */
export const kanalName = (k: ReviewTarget): string => KANAL_NAME[k] ?? k;
export const kanalKurz = (k: ReviewTarget): string => KANAL_KURZ[k] ?? k;

/**
 * Farbbalken PLUS Wort. Die Farbe steht nie allein — sie trägt hier keine
 * Information, die das Wort nicht auch trägt.
 */
export function ChannelBar({ channel }: { channel: ReviewTarget }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-2">
      <span
        aria-hidden
        className="inline-block h-4 w-[3px] rounded-full"
        style={{ backgroundColor: KANAL_FARBE[channel] }}
      />
      <span className="text-sm" style={{ color: t.textSecondary }}>{KANAL_NAME[channel]}</span>
    </span>
  );
}

/**
 * Das Kürzel des Repo-Ordners (`E31`) als kleiner Monospace-Chip vor dem Titel
 * (CUR-2287, Spec §16.4). Es ist das, wonach Fabian sucht, wenn er vom
 * Dateisystem kommt — deshalb steht es vorn und nicht im Grau dahinter.
 *
 * Ohne Kürzel kein Platzhalter: Ein „—" in jeder Entwurfszeile läse sich wie
 * ein Fehler, und das Cockpit sagt ohnehin, dass der Ordner fehlt.
 */
export function KuerzelChip({ kuerzel, ordner, gross }: {
  kuerzel: string | null | undefined;
  /** Voller Pfad als Tooltip — Zusatz, nicht Ersatz: das Cockpit zeigt ihn ausgeschrieben. */
  ordner?: string | null;
  gross?: boolean;
}): JSX.Element | null {
  if (!kuerzel) return null;
  return (
    <span
      data-kuerzel
      title={ordner ?? undefined}
      className={`inline-block shrink-0 rounded font-mono font-semibold ${gross ? 'px-2 py-0.5 text-lg' : 'px-1.5 py-0.5 text-xs'}`}
      style={{ backgroundColor: t.bgElevated, color: t.textSecondary }}
    >
      {kuerzel}
    </span>
  );
}

/** `2026-09-08` → `Di 08.09.` — im Kalender liest man Wochentage, nicht ISO. */
export function tagKurz(datum: string): string {
  const d = new Date(`${datum}T00:00:00.000Z`);
  const wd = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][d.getUTCDay()] ?? '';
  return `${wd} ${String(d.getUTCDate()).padStart(2, '0')}.${String(d.getUTCMonth() + 1).padStart(2, '0')}.`;
}

/** Sekunden → `0:57` bzw. `11:19`. */
export function dauer(sek: number | null): string {
  if (sek === null) return '—';
  const m = Math.floor(sek / 60);
  return `${m}:${String(Math.round(sek % 60)).padStart(2, '0')}`;
}

/** Die Reichweite als Satz. Drei Fälle, drei Sätze — nie „—" für zwei davon. */
export function reichweiteText(r: Reichweite): string {
  if (r.art === 'leer') return 'Vorrat leer';
  if (r.art === 'ueber-horizont') return 'trägt über ein Jahr';
  return `trägt bis ${tagKurz(r.datum)}`;
}

/**
 * Leerzustand mit einem Befehl zum Kopieren statt einer leeren Fläche.
 *
 * Ein leerer Kalender ist fast immer „noch nicht synchronisiert", nicht „nichts
 * geplant". Die Fläche, die das nicht sagt, lässt einen im Dashboard nach einem
 * Fehler suchen, der woanders liegt.
 */
export function EmptyHint({ title, command }: { title: string; command?: string }): JSX.Element {
  return (
    <div className="rounded-md border p-6" style={{ borderColor: t.borderSubtle, backgroundColor: t.bgSurface }}>
      <p className="mb-3 text-sm" style={{ color: t.textSecondary }}>{title}</p>
      {command && <CopyField label="Sync-Lauf" value={command} mono />}
    </div>
  );
}
